import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Brand } from "@/components/Brand";
import { authConfig, authGoogle } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GSI_SRC = "https://accounts.google.com/gsi/client";

function loadGoogleScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let el = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (!el) {
      el = document.createElement("script");
      el.src = GSI_SRC;
      el.async = true;
      document.head.appendChild(el);
    }
    el.addEventListener("load", () => resolve());
    el.addEventListener("error", () => reject(new Error("Could not load Google Sign-In")));
  });
}

export default function Login() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser, setStatus } = useAuth();
  const buttonRef = useRef(null);
  const [err, setErr] = useState(loc.state?.error || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { google_client_id } = await authConfig();
        if (!google_client_id) throw new Error("Google Sign-In is not configured on the server.");
        await loadGoogleScript();
        if (cancelled || !buttonRef.current) return;
        window.google.accounts.id.initialize({
          client_id: google_client_id,
          callback: async ({ credential }) => {
            setBusy(true);
            setErr(null);
            try {
              const { user } = await authGoogle(credential);
              setUser(user);
              setStatus(true);
              nav(loc.state?.from || "/", { replace: true });
            } catch (e) {
              setErr(e.response?.data?.detail || "Sign-in failed. Please try again.");
              setBusy(false);
            }
          },
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: "outline", size: "large", shape: "pill", text: "continue_with",
          width: buttonRef.current.offsetWidth || 320,
        });
      } catch (e) {
        if (!cancelled) setErr(e.message || "Could not load Google Sign-In.");
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100 grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-[#1f1f1f] relative overflow-hidden">
        <Brand size={44} />

        <div className="max-w-md">
          <div className="eyebrow">Gym management</div>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter mt-3 leading-[1.02]">
            Run your gym like a real business.
          </h1>
          <p className="text-zinc-400 mt-4 leading-relaxed">
            One place for front-end sales, renewals, customers, expenses, payroll and business intelligence —
            with month-on-month deltas, ROAS, CAC and everything that decides whether you win the month.
          </p>

          <ul className="mt-8 space-y-3 text-sm text-zinc-300">
            {["Front End Sales & All Sales in a single ledger",
              "Live expenses, payroll & profit dashboard",
              "Cost per lead, CAC, ROAS and all-in cost per sale",
              "Role-based access for User, Manager, Admin, Master Admin"].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="text-[11px] text-zinc-600">
          Front End Sales · All Sales ledger · Expenses · P&L · BI · PDF export
        </div>
      </div>

      <div className="flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center mb-8">
            <Brand size={36} />
          </div>

          <div className="eyebrow">Get started</div>
          <h2 className="font-display text-3xl font-extrabold tracking-tight mt-2">Sign in to FullPowerAnalytics</h2>

          {err && (
            <div className="mt-4 border border-rose-500/30 bg-rose-500/5 text-rose-300 text-sm rounded-lg p-3" data-testid="login-error">
              {err}
            </div>
          )}

          <div className="mt-8 w-full min-h-[44px] flex justify-center" data-testid="google-signin-btn">
            {busy && <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />}
            {/* Kept mounted while busy so Google's rendered button survives a failed attempt. */}
            <div ref={buttonRef} className={busy ? "hidden" : "w-full flex justify-center"} />

          </div>

          <p className="text-[11px] text-zinc-500 mt-6 leading-relaxed text-center">
            By continuing you agree that FullPowerAnalytics may store your sales, customer and expense data to compute business metrics.
          </p>
        </div>
      </div>
    </div>
  );
}

